import { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/feature/EmptyState";
import Toast from "@/components/base/Toast";
import VideoPlayer from "@/pages/detail/components/VideoPlayer";
import SidebarList from "@/pages/detail/components/SidebarList";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  formatDate,
  formatDuration,
  formatViews,
  getCategoryName,
  getCategoryVideos,
  getRelatedVideos,
  getVideoById,
} from "@/utils/video";

const shareTargets = [
  { key: "weibo", label: "微博", icon: "ri-weibo-line" },
  { key: "wechat", label: "微信", icon: "ri-wechat-line" },
  { key: "x", label: "X", icon: "ri-twitter-x-line" },
];

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const video = getVideoById(id);
  const [toast, setToast] = useState("");
  const closeToast = useCallback(() => setToast(""), []);

  useDocumentMeta({
    title: video ? `${video.title} - 亚洲激情视频详情` : "视频不存在 - 亚洲激情视频",
    description: video
      ? `${video.summary} 亚洲激情视频提供${getCategoryName(video.category)}频道视频详情、基本信息与相关推荐。`
      : "该视频不存在或已下架，返回亚洲激情视频首页浏览其他精选内容。",
    keywords: video
      ? `${video.title},${getCategoryName(video.category)},${video.tags.join(",")},亚洲激情视频`
      : "亚洲激情视频,视频详情",
  });

  const related = video ? getRelatedVideos(video, 4) : [];
  const sameCategory = video
    ? getCategoryVideos(video.category)
        .filter((item) => item.id !== video.id)
        .slice(0, 4)
    : [];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  if (!video) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-[1440px] px-4 py-20 md:px-8">
          <EmptyState
            icon="ri-file-unknow-line"
            title="内容不存在"
            description="你访问的视频可能已调整或不存在。可以返回首页，或者用搜索找找其他片目。"
          />
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-500"
            >
              <i className="ri-home-4-line text-sm" />
              返回首页
            </Link>
            <Link
              to="/search"
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-5 py-2.5 text-sm text-foreground-200 transition-colors hover:border-primary-600 hover:text-primary-200"
            >
              <i className="ri-search-line text-sm" />
              去搜索
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: getCategoryName(video.category), to: `/category/${video.category}` },
            { label: video.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <VideoPlayer video={video} />

            <h1 className="mt-7 font-heading text-2xl font-bold leading-snug text-foreground-50 md:text-3xl">
              {video.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-400">
              <Link
                to={`/category/${video.category}`}
                className="rounded-full border border-primary-800 bg-primary-950/60 px-2.5 py-1 text-primary-200 transition-colors hover:border-primary-600"
              >
                {getCategoryName(video.category)}
              </Link>
              <span className="rounded-full border border-background-700 px-2.5 py-1">{video.type}</span>
              <span className="flex items-center gap-1 tabular-nums">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-time-line text-xs" />
                </span>
                {formatDuration(video.duration)}
              </span>
              <span className="flex items-center gap-1 tabular-nums">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-calendar-line text-xs" />
                </span>
                {formatDate(video.publishedAt)}
              </span>
              <span className="flex items-center gap-1 tabular-nums">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-eye-line text-xs" />
                </span>
                {formatViews(video.views)}
              </span>
            </div>

            <section className="mt-6 rounded-lg border border-background-800 bg-background-900 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-100">
                <span className="h-3.5 w-1 rounded-full bg-primary-500" />
                视频简介
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-400">{video.description}</p>
              <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                以上简介为虚构示例文案，仅用于展示详情页信息结构，不对应任何真实作品或人物。
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-600">标签：</span>
                {video.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded bg-secondary-900/70 px-2 py-1 text-[11px] text-secondary-200 transition-colors hover:bg-secondary-800"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </section>

            <div className="mt-5 flex flex-col gap-3 rounded-lg border border-background-800 bg-background-900 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium text-foreground-100">分享这个页面</p>
                <p className="mt-1 text-xs text-foreground-500">
                  分享为演示功能，仅复制站内链接，不会跳转外部平台。
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-500"
                >
                  <i className="ri-link" />
                  复制链接
                </button>
                {shareTargets.map((target) => (
                  <button
                    key={target.key}
                    type="button"
                    onClick={() => setToast(`演示模式：暂未接入${target.label}分享`)}
                    aria-label={`分享到${target.label}`}
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-700 text-foreground-300 transition-colors hover:border-primary-600 hover:text-primary-200"
                  >
                    <i className={`${target.icon} text-sm`} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-800 bg-background-900">
              <h2 className="border-b border-background-800 px-4 py-3 font-heading text-sm font-semibold text-foreground-100">
                视频信息
              </h2>
              <dl className="divide-y divide-background-800/70 text-xs">
                {[
                  { label: "视频类型", value: video.type },
                  { label: "所属分类", value: getCategoryName(video.category) },
                  { label: "视频时长", value: formatDuration(video.duration) },
                  { label: "发布日期", value: formatDate(video.publishedAt) },
                  { label: "站内播放", value: formatViews(video.views) },
                  { label: "内容编号", value: video.id.toUpperCase() },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between gap-4 px-4 py-2.5">
                    <dt className="text-foreground-600">{row.label}</dt>
                    <dd className="text-right text-foreground-200">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="border-t border-background-800 px-4 py-3">
                <p className="flex items-start gap-2 text-[11px] leading-relaxed text-foreground-600">
                  <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    <i className="ri-alert-line text-accent-400" />
                  </span>
                  本页为演示数据，播放功能为前端模拟，不保证真实播放。
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-background-800 bg-background-900">
              <div className="flex items-center justify-between border-b border-background-800 px-4 py-3">
                <h2 className="font-heading text-sm font-semibold text-foreground-100">同分类推荐</h2>
                <Link
                  to={`/category/${video.category}`}
                  className="flex items-center gap-1 text-[11px] text-foreground-500 transition-colors hover:text-primary-300"
                >
                  全部
                  <i className="ri-arrow-right-s-line text-sm" />
                </Link>
              </div>
              <SidebarList items={sameCategory} />
            </div>
          </aside>
        </div>

        <section className="mt-14">
          <SectionHeading
            eyebrow="You may also like"
            title="相关推荐视频"
            description="根据标签与分类相似度整理，越靠前的作品与当前内容越接近。"
            actionText="查看更多"
            actionTo={`/category/${video.category}`}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((item) => (
              <VideoCard key={item.id} video={item} />
            ))}
          </div>
        </section>
      </div>

      {toast && <Toast message={toast} onClose={closeToast} />}
    </SiteLayout>
  );
}